import { LandmarkData, ImagesData } from "../types"; // Assuming types are moved or defined here
// Import the centralized URIs from the specified path
import { mobileAppUri, dataLoaderUri } from "../API";
import apiClient from "../../services/apiClient";

// Removed the old local constants API_BASE_URL_GET and API_BASE_URL_PUT

export const fetchLandmarks = async (
  courseId: string,
  targLangCode: string
): Promise<LandmarkData[]> => {
  try {
    console.log("🔍 [fetchLandmarks] Starting request with params:", { courseId, targLangCode });
    
    // Method: GET -> Use mobileAppUri with authenticated API client
    const jsonResponse = await apiClient.get(
      `${mobileAppUri}/api/v1/landmarks`,
      { params: { courseId, Targ_Lang_Code: targLangCode } }
    );
    console.log("✅ [fetchLandmarks] Response received:", jsonResponse);

    if (!jsonResponse.Landmarks || !Array.isArray(jsonResponse.Landmarks)) {
      throw new Error("Invalid API response: 'Landmarks' array not found.");
    }

    const landmarksArray = jsonResponse.Landmarks;

    const transformedData: LandmarkData[] = landmarksArray.map(
      (item: any): LandmarkData => {
        // Basic validation for core fields expected from GET
        const requiredFields = ["Identifier", "City", "Country", "Images"]; // Geo_Coordinates and Description handled below
        const missingFields = requiredFields.filter(
          (field) => item[field] === undefined || item[field] === null
        );
        if (missingFields.length > 0) {
          console.warn(
            `API item missing fields: ${missingFields.join(
              ", "
            )} for Identifier: ${item.Identifier || "Unknown"}`
          );
          // Decide if this should be an error or just a warning. For now, warning.
          // throw new Error(`Invalid API response structure: Missing fields: ${missingFields.join(", ")}`);
        }

        let images: ImagesData[] = [];
        try {
          const rawImages =
            typeof item.Images === "string"
              ? JSON.parse(item.Images)
              : item.Images;

          if (Array.isArray(rawImages)) {
            images = rawImages
              .map((img: any) => ({
                URL: img.URL || "", // Ensure URL exists
                Description: img.Description || "",
              }))
              .filter((img) => img.URL); // Filter out images without a URL
          } else if (rawImages) {
            // Handle cases where Images might be a single object or malformed
            console.warn(
              `Images field is not an array for Identifier ${item.Identifier}:`,
              rawImages
            );
          }
        } catch (e) {
          console.error(
            `Error parsing Images for Identifier ${item.Identifier}:`,
            item.Images,
            e
          );
          // Decide if this should throw an error or proceed with empty images
          // throw new Error("Invalid Images format in API response");
        }

        // Handle Coordinates safely
        const geo = item.Geo_Coordinates;
        let coordinates: [number, number] = [0, 0]; // Default coordinates
        let geoCoordinatesData: any = {}; // Default empty object
        if (geo && typeof geo === "object") {
          const lat = Number(geo.lat);
          const lon = Number(geo.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            coordinates = [lon, lat];
            geoCoordinatesData = { lat, lon };
          } else {
            console.warn(
              `Invalid Geo_Coordinates format for Identifier ${item.Identifier}:`,
              geo
            );
          }
        } else if (geo) {
          console.warn(
            `Geo_Coordinates is not an object for Identifier ${item.Identifier}:`,
            geo
          );
        }

        // Choose the best available description
        const description =
          item.Target_Lang_Description ||
          item.Base_Lang_Description ||
          item.Description ||
          "";

        return {
          Identifier: item.Identifier || `missing-id-${Math.random()}`, // Provide a fallback ID if missing
          City: item.City || "Unknown City",
          Country: item.Country || "Unknown Country",
          Description: description,
          Images: images,
          Geo_Coordinates: geoCoordinatesData,
          // Include optional fields if they exist in the response, otherwise undefined
          Site: item.Site,
          Subtitle: item.Subtitle,
          // Optionally add the computed safe coordinates for easier use downstream:
          coordinates, // computed [lon, lat] array
        };
      }
    );

    console.log("Transformed landmarks:", transformedData);
    return transformedData;
  } catch (error) {
    console.error("Error fetching landmarks:", error);
    throw error; // Re-throw the error to be caught by the component
  }
};

// --- NEW FUNCTION TO UPDATE LANDMARKS ---
// Method: PUT -> Use dataLoaderUri
export const updateLandmarks = async (
  targLangCode: string,
  domain: string,
  landmarks: LandmarkData[]
): Promise<void> => {
  // Returns void on success, throws error on failure
  const url = `${dataLoaderUri}/api/v1/edit-landmarks`; // Added api/v1/ prefix

  // Prepare the payload according to the API specification
  const payload = {
    Targ_Lang_Code: targLangCode,
    Domain: domain,
    // Ensure the Landmarks array matches the expected format.
    // Map the data, potentially excluding computed fields like 'coordinates'
    // and ensuring essential fields expected by the PUT endpoint are present.
    Landmarks: landmarks.map((lm) => ({
      Identifier: lm.Identifier,
      City: lm.City,
      Country: lm.Country,
      Description: lm.Description,
      Site: lm.Site || "", // Include Site, default to empty string if undefined
      Subtitle: lm.Subtitle || "", // Include Subtitle, default to empty string if undefined
      Geo_Coordinates: lm.Geo_Coordinates || {}, // Ensure it's an object
      Images: lm.Images.map((img) => ({
        // Ensure Images format is correct
        URL: img.URL,
        Description: img.Description || "", // Send description even if empty
      })),
    })),
  };

  console.log("Sending PUT request to:", url);
  console.log("PUT request payload:", JSON.stringify(payload, null, 2)); // Log the payload for debugging

  try {
    await apiClient.put(url, payload);
    console.log("Update successful");

    // If no error, the promise resolves successfully (implicitly returns void)
  } catch (error) {
    console.error("Error updating landmarks:", error);
    // Re-throw the error so the calling component can handle it
    throw error;
  }
};
